import React, { useState, useEffect, useRef } from 'react';
import { Button, notification } from 'antd';
import { Modal } from '_src/components';
import { Select, Input } from '_src/shared/form-helpers';
import { useSave } from 'store';
import services from '_src/services';

import { ModalContent, FormRow, Label } from '../styles';

const laptopConfig = services['laptopLending'];

export default function TransferLaptopModal({
  visible,
  onClose,
  laptop,
  currentBranchId,
  branches = [],
  onTransferred
}) {
  const [form, setForm] = useState({
    toBranchId: null,
    reason: ''
  });
  const [saving, setSaving] = useState(false);
  const laptopRef = useRef(null);

  const { save: transferLaptop } = useSave(laptopConfig.transferLaptop);

  useEffect(() => {
    if (visible && laptop) {
      laptopRef.current = laptop;
      setForm({ toBranchId: null, reason: '' });
    }
  }, [visible, laptop]);

  const currentLaptop = laptopRef.current;

  const handleTransfer = async () => {
    if (!currentLaptop?.id) {
      notification.error({ message: 'Invalid laptop selected', duration: 3 });
      return;
    }

    if (!form.toBranchId) {
      notification.warning({ message: 'Please select a destination branch', duration: 3 });
      return;
    }

    if (form.toBranchId === currentBranchId) {
      notification.warning({ message: 'Please select a different branch', duration: 3 });
      return;
    }

    setSaving(true);

    const response = await transferLaptop({
      laptopId: currentLaptop.id,
      to_branch_id: form.toBranchId,
      reason: form.reason
    });

    setSaving(false);

    if (response?.success === 1) {
      notification.success({ message: 'Laptop transferred successfully', duration: 5 });
      laptopRef.current = null;
      onTransferred();
      onClose();
    } else {
      notification.error({
        message: response?.data?.message || 'Failed to transfer laptop',
        duration: 5
      });
    }
  };

  const laptopName = `${currentLaptop?.brand || ''} ${currentLaptop?.model || ''}`.trim();
  const serialNumber = currentLaptop?.serialNumber || currentLaptop?.serial_number || '';

  const availableBranches = branches
    .filter(b => b.id !== currentBranchId)
    .map(b => ({ id: b.id, label: b.name }));

  return (
    <Modal
      title="Transfer Laptop to Another Branch"
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="transfer" type="primary" onClick={handleTransfer} loading={saving}>
          Transfer Laptop
        </Button>
      ]}
      width={500}
    >
      <ModalContent>
        <FormRow>
          <Label>Laptop</Label>
          <div style={{ padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
            {laptopName} {serialNumber && `(${serialNumber})`}
          </div>
        </FormRow>

        <FormRow>
          <Label>Current Branch</Label>
          <div style={{ padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
            {branches.find(b => b.id === currentBranchId)?.name || 'Unknown'}
          </div>
        </FormRow>

        <FormRow>
          <Label>Transfer To *</Label>
          <Select
            placeholder="Select destination branch"
            options={availableBranches}
            value={form.toBranchId}
            onChange={value => setForm(prev => ({ ...prev, toBranchId: value }))}
            style={{ width: '100%' }}
          />
        </FormRow>

        <FormRow>
          <Label>Reason</Label>
          <Input
            name="reason"
            placeholder="Optional reason for transfer"
            value={form.reason}
            onChange={value => setForm(prev => ({ ...prev, reason: value }))}
          />
        </FormRow>
      </ModalContent>
    </Modal>
  );
}
